// =============================================================================
// Step 2 — Smart Constraints (time window, days, resource preferences)
// =============================================================================

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Briefcase, Filter, Trash2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { DatePickerInput } from '../../ui/DatePickerInput';
import { ResourceCombobox } from '../../ui/ResourceCombobox';
import { L } from '../../../config/labels';
import { useBooking } from './BookingContext';
import type { ResourceRequirementForPanel, ExpandedSlot } from './types';
import { DAY_LABELS } from './types';
import { useTenantTimeZone } from '../../../store/tenantStore';
import { lastInstantOfCivilDay, startOfCivilDayInTz } from '../../../lib/tenantTime';

// ===========================================================================
// Main Step Component
// ===========================================================================

export function SmartConstraintsStep() {
  const { t } = useTranslation('scheduler');
  const { state, selectedService, resourceTypes, allResources } = useBooking();

  // ── Derive resource requirements for the panel ───────────────────────
  const resourceRequirements = useMemo<ResourceRequirementForPanel[]>(() => {
    if (state.bookingMode === 'catalog') {
      return (selectedService?.requirements || []) as ResourceRequirementForPanel[];
    }
    return state.customEvent.resourceRequirements.map((r, i) => ({
      id: `custom-${i}`,
      resourceTypeId: r.resourceTypeId,
      quantity: r.quantity,
      resourceType: resourceTypes.find(rt => rt.id === r.resourceTypeId) as
        | { id: string; name: string }
        | undefined,
    }));
  }, [
    state.bookingMode,
    selectedService?.requirements,
    state.customEvent.resourceRequirements,
    resourceTypes,
  ]);

  return (
    <div className="space-y-6">
      {/* Summary of selection */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1">
            <div className="text-sm text-gray-500">
              {state.bookingMode === 'catalog' ? L.service : t('booking.customEvent')}
            </div>
            <div className="font-medium">
              {state.bookingMode === 'catalog'
                ? selectedService?.name
                : state.customEvent.title}
            </div>
            <div className="text-xs text-gray-400">
              {state.bookingMode === 'catalog'
                ? `${selectedService?.durationMinutes} min`
                : `${state.customEvent.duration} min`}
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="text-center py-2">
        <div className="flex items-center justify-center gap-2 text-indigo-700 font-medium text-sm">
          <Filter className="w-4 h-4" />
          {t('constraints.title')}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {t('constraints.description')}
        </p>
      </div>

      {/* Constraints Panel */}
      <SmartConstraintsPanel
        resourceRequirements={resourceRequirements}
        allResources={allResources}
      />
    </div>
  );
}

// ===========================================================================
// SmartConstraintsPanel (days, time range, deadline, resource overrides)
// ===========================================================================

interface SmartConstraintsPanelProps {
  resourceRequirements: ResourceRequirementForPanel[];
  allResources: Array<{
    id: string;
    name: string;
    resourceTypeId: string;
    isActive: boolean;
    isBookable: boolean;
  }>;
}

function SmartConstraintsPanel({
  resourceRequirements,
  allResources,
}: SmartConstraintsPanelProps) {
  const { t } = useTranslation('scheduler');
  const { state, dispatch } = useBooking();
  const { smartConstraints } = state;
  const timeZone = useTenantTimeZone();

  const hasActiveConstraints =
    smartConstraints.allowedDays.length > 0 ||
    !!smartConstraints.minTime ||
    !!smartConstraints.maxTime ||
    !!smartConstraints.startFromDate ||
    !!smartConstraints.deadlineDate ||
    smartConstraints.overriddenRequirements.some(r => r.resourceId !== null);

  // Expand requirements by quantity into individual slot rows
  // e.g. { resourceTypeId: 'TECH', quantity: 2 } → "Tecnico 1", "Tecnico 2"
  const expandedSlots = useMemo<ExpandedSlot[]>(() => {
    const slots: ExpandedSlot[] = [];
    let globalIdx = 0;
    for (const req of resourceRequirements) {
      const typeName = req.resourceType?.name || L.resource;
      for (let q = 0; q < req.quantity; q++) {
        slots.push({
          key: `${req.resourceTypeId}-${q}`,
          resourceTypeId: req.resourceTypeId,
          label: req.quantity > 1 ? `${typeName} ${q + 1}` : typeName,
          slotIndex: globalIdx,
        });
        globalIdx++;
      }
    }
    return slots;
  }, [resourceRequirements]);

  // Sync overriddenRequirements array size when expanded slots change.
  // The reducer's SYNC_OVERRIDDEN_REQUIREMENTS bails out if nothing changed,
  // so this effect is safe and won't loop.
  useEffect(() => {
    dispatch({ type: 'SYNC_OVERRIDDEN_REQUIREMENTS', payload: expandedSlots });
  }, [expandedSlots, dispatch]);

  return (
    <div className="space-y-5">
      {/* Giorni consentiti */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-2 block">
          {t('constraints.allowedDays')}
        </label>
        <div className="flex flex-wrap gap-1.5">
          {DAY_LABELS.map(day => {
            const isActive = smartConstraints.allowedDays.includes(day.value);
            return (
              <button
                key={day.value}
                type="button"
                onClick={() => {
                  const newDays = isActive
                    ? smartConstraints.allowedDays.filter(d => d !== day.value)
                    : [...smartConstraints.allowedDays, day.value];
                  dispatch({ type: 'UPDATE_CONSTRAINTS', payload: { allowedDays: newDays } });
                }}
                className={cn(
                  'px-3 py-1.5 rounded-full text-xs font-medium transition-all border',
                  isActive
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400',
                )}
              >
                {t(`common:${day.labelKey}`)}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-gray-400 mt-1">
          {t('constraints.allowedDaysHint')}
        </p>
      </div>

      {/* Fascia oraria */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-2 block">{t('constraints.timeRange')}</label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-gray-500 mb-0.5 block">{t('constraints.fromTime')}</label>
            <input
              type="time"
              value={smartConstraints.minTime}
              onChange={e =>
                dispatch({ type: 'UPDATE_CONSTRAINTS', payload: { minTime: e.target.value } })
              }
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500 mb-0.5 block">{t('constraints.toTime')}</label>
            <input
              type="time"
              value={smartConstraints.maxTime}
              onChange={e =>
                dispatch({ type: 'UPDATE_CONSTRAINTS', payload: { maxTime: e.target.value } })
              }
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* Date range: from / to */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-2 block">
          {t('constraints.dateRange')}
        </label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-gray-500 mb-0.5 block">
              {t('constraints.startFrom')}
            </label>
            <DatePickerInput
              value={
                smartConstraints.startFromDate ? smartConstraints.startFromDate.split('T')[0] : ''
              }
              onChange={(val) =>
                dispatch({
                  type: 'UPDATE_CONSTRAINTS',
                  payload: {
                    // Il giorno scelto e' un giorno del calendario
                    // dell'organizzazione: convertirlo con la mezzanotte del
                    // browser sposterebbe il vincolo di ore o di un giorno.
                    startFromDate: val
                      ? startOfCivilDayInTz(val, timeZone).toISOString()
                      : '',
                  },
                })
              }
              size="sm"
            />
          </div>
          <div>
            <label className="text-[10px] text-gray-500 mb-0.5 block">
              {t('constraints.deadline')}
            </label>
            <DatePickerInput
              value={
                smartConstraints.deadlineDate ? smartConstraints.deadlineDate.split('T')[0] : ''
              }
              onChange={(val) =>
                dispatch({
                  type: 'UPDATE_CONSTRAINTS',
                  payload: {
                    deadlineDate: val
                      ? lastInstantOfCivilDay(val, timeZone).toISOString()
                      : '',
                  },
                })
              }
              size="sm"
            />
          </div>
        </div>
      </div>

      {/* Preferenze risorse — expanded per-slot ("Tecnico 1", "Tecnico 2") */}
      {expandedSlots.length > 0 && (
        <div>
          <label className="text-xs font-medium text-gray-600 mb-2 block">
            {t('constraints.resourcePreferences')}
          </label>
          <div className="space-y-2">
            {expandedSlots.map(slot => {
              // Risorse già scelte in ALTRI slot dello stesso tipo: una stessa risorsa
              // non può coprire due requisiti distinti (es. 2 tecnici = 2 persone diverse).
              const takenByOthers = new Set(
                expandedSlots
                  .filter(
                    s =>
                      s.resourceTypeId === slot.resourceTypeId &&
                      s.slotIndex !== slot.slotIndex,
                  )
                  .map(s => smartConstraints.overriddenRequirements[s.slotIndex]?.resourceId)
                  .filter((id): id is string => !!id),
              );
              const resourcesOfType = allResources.filter(
                r =>
                  r.resourceTypeId === slot.resourceTypeId &&
                  r.isActive &&
                  r.isBookable &&
                  !takenByOthers.has(r.id),
              );
              const currentValue =
                smartConstraints.overriddenRequirements[slot.slotIndex]?.resourceId || '';

              return (
                <div key={slot.key} className="flex items-center gap-2">
                  <span
                    className="text-xs text-gray-500 w-24 truncate"
                    title={slot.label}
                  >
                    {slot.label}:
                  </span>
                  <ResourceCombobox
                    resources={resourcesOfType}
                    value={currentValue}
                    onChange={newId =>
                      dispatch({
                        type: 'SET_RESOURCE_OVERRIDE',
                        payload: { slotIndex: slot.slotIndex, resourceId: newId || null },
                      })
                    }
                    size="sm"
                    className="flex-1"
                    placeholder={t('constraints.searchResource')}
                    emptyLabel={t('constraints.any')}
                    noMatchLabel={t('constraints.resourceNoMatch')}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Reset vincoli */}
      {hasActiveConstraints && (
        <button
          type="button"
          onClick={() => dispatch({ type: 'RESET_CONSTRAINTS' })}
          className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
        >
          <Trash2 className="w-3 h-3" />
          {t('constraints.clearAll')}
        </button>
      )}
    </div>
  );
}
